/**
 * Floating Action Button component
 *
 * Creates a floating button that can trigger modals or navigate to links.
 * Integrates with the existing Modal system via event bus.
 */
export default class FloatingButton {
  /**
   * @param {Object} app - Main application instance with event bus
   * @param {Object} options - Configuration options
   * @param {string} options.text - Button text
   * @param {string} options.icon - SVG icon markup
   * @param {string} options.modalSlug - Slug of modal to open on click
   * @param {string} options.link - URL to navigate to (if no modalSlug)
   * @param {string} options.position - Button position: 'bottom-right' | 'bottom-left' | 'bottom-center'
   * @param {number} options.delay - Delay in seconds before showing button
   * @param {boolean} options.hideAfterDismissed - Hide button if linked modal was dismissed
   * @param {string} options.animation - Animation style: 'bounce' | 'pulse' | 'slide-in' | 'none'
   * @param {string} options.colorTheme - Color theme: 'primary' | 'secondary' | 'green'
   * @param {string} options.buttonStyle - Button style: 'floating' | 'bordered' | 'secondary' | 'arrow'
   * @param {boolean} options.hideOnScroll - Hide button when scrolling down
   */
  constructor(app, options = {}) {
    this.app = app
    this.options = {
      text: options.text || '',
      icon: options.icon || null,
      modalSlug: options.modalSlug || null,
      link: options.link || null,
      linkTarget: options.linkTarget || '_self',
      position: options.position || 'bottom-right',
      delay: options.delay || 0,
      hideAfterDismissed: options.hideAfterDismissed || false,
      animation: options.animation || 'none',
      colorTheme: options.colorTheme || 'primary',
      buttonStyle: options.buttonStyle || 'floating',
      hideOnScroll: options.hideOnScroll || false
    }

    this.el = null
    this.visible = false
    this.lastScrollY = 0
    this.scrollTimeout = null

    if (this.shouldShow()) {
      this.init()
    }
  }

  /**
   * Check if button should be displayed based on dismissed modals
   */
  shouldShow() {
    if (!this.options.hideAfterDismissed || !this.options.modalSlug) {
      return true
    }

    // Use existing cookie system from Modal class
    const dismissed = window.cookies?.getCookie('dismissed-modals')?.split(',') || []
    return !dismissed.includes(this.options.modalSlug)
  }

  /**
   * Initialize the floating button
   */
  init() {
    this.el = this.createButton()

    // Show button after delay
    setTimeout(() => {
      document.body.appendChild(this.el)
      // Trigger animation on next frame
      requestAnimationFrame(() => {
        this.el.classList.add('is-visible')
        this.visible = true
      })
    }, this.options.delay * 1000)

    // Setup scroll hiding if enabled
    if (this.options.hideOnScroll) {
      this.setupScrollListener()
    }

    // Listen for modal dismiss to potentially hide button
    if (this.options.hideAfterDismissed && this.options.modalSlug) {
      this.app.bus.on('modalDismissed', (name) => {
        if (name === this.options.modalSlug) {
          this.hide()
        }
      })
    }
  }

  /**
   * Create button DOM element
   */
  createButton() {
    const btn = document.createElement('button')
    const style = this.options.buttonStyle

    // Use existing .btn styles or floating-button styles
    if (style === 'floating') {
      btn.className = 'floating-button'
      btn.className += ` floating-button--${this.options.position}`
      btn.className += ` floating-button--${this.options.colorTheme}`

      if (this.options.animation !== 'none') {
        btn.className += ` floating-button--${this.options.animation}`
      }

      // Add icon if provided
      if (this.options.icon) {
        const iconWrapper = document.createElement('span')
        iconWrapper.className = 'floating-button__icon'
        iconWrapper.innerHTML = this.options.icon
        btn.appendChild(iconWrapper)
      }

      // Add text if provided
      if (this.options.text) {
        const textWrapper = document.createElement('span')
        textWrapper.className = 'floating-button__text'
        textWrapper.textContent = this.options.text
        btn.appendChild(textWrapper)
      }
    } else {
      // Use existing .btn styles (bordered, secondary, arrow)
      btn.className = 'floating-button-wrapper btn'
      btn.className += ` floating-button-wrapper--${this.options.position}`

      if (style === 'secondary') {
        btn.className += ' is-style-secondary'
      } else if (style === 'arrow') {
        btn.className += ' is-style-arrow'
      }

      if (this.options.animation !== 'none') {
        btn.className += ` floating-button-wrapper--${this.options.animation}`
      }

      // Create inner link element (matches .btn structure)
      const link = document.createElement('span')
      link.className = 'btn__link'

      // Add arrow element for arrow style
      if (style === 'arrow') {
        const arrow = document.createElement('span')
        arrow.className = 'btn__arrow'
        btn.appendChild(arrow)
      }

      // Add icon if provided (before text)
      if (this.options.icon) {
        const iconWrapper = document.createElement('span')
        iconWrapper.className = 'floating-button__icon'
        iconWrapper.innerHTML = this.options.icon
        link.appendChild(iconWrapper)
      }

      // Add text
      if (this.options.text) {
        link.appendChild(document.createTextNode(this.options.text))
      }

      btn.appendChild(link)
    }

    // Add aria label for accessibility
    btn.setAttribute('aria-label', this.options.text || 'Open dialog')
    btn.setAttribute('type', 'button')

    // Attach click handler
    btn.addEventListener('click', (e) => this.handleClick(e))

    return btn
  }

  /**
   * Handle button click
   */
  handleClick(e) {
    e.preventDefault()

    // Track click in GTM if available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'floatingButtonClick',
        floatingButtonText: this.options.text,
        floatingButtonModal: this.options.modalSlug,
        floatingButtonLink: this.options.link
      })
    }

    if (this.options.modalSlug) {
      // Use existing event bus to open modal
      this.app.bus.emit('openModal', this.options.modalSlug)
    } else if (this.options.link) {
      if (this.options.linkTarget === '_blank') {
        window.open(this.options.link, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = this.options.link
      }
    }
  }

  /**
   * Setup scroll listener for hide on scroll functionality
   */
  setupScrollListener() {
    this.lastScrollY = window.scrollY

    window.addEventListener('scroll', () => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }

      this.scrollTimeout = setTimeout(() => {
        const currentScrollY = window.scrollY
        const scrollingDown = currentScrollY > this.lastScrollY
        const scrollDistance = Math.abs(currentScrollY - this.lastScrollY)

        // Only react to significant scroll (> 50px)
        if (scrollDistance > 50) {
          if (scrollingDown && this.visible) {
            this.el.classList.add('is-hidden-scroll')
          } else if (!scrollingDown) {
            this.el.classList.remove('is-hidden-scroll')
          }
        }

        this.lastScrollY = currentScrollY
      }, 50)
    }, { passive: true })
  }

  /**
   * Show the button
   */
  show() {
    if (this.el && !this.visible) {
      this.el.classList.add('is-visible')
      this.el.classList.remove('is-hidden-scroll')
      this.visible = true
    }
  }

  /**
   * Hide the button with animation
   */
  hide() {
    if (this.el && this.visible) {
      this.el.classList.remove('is-visible')
      this.visible = false

      // Remove from DOM after animation
      setTimeout(() => {
        this.el?.remove()
      }, 400)
    }
  }

  /**
   * Destroy the button instance
   */
  destroy() {
    this.el?.remove()
    this.el = null
    this.visible = false
  }
}
